import { createTheme } from "@mui/material/styles";

// ===== Brand tokens =====
// Sidebar stays a deep professional navy (corporate, trustworthy).
export const NAVY = "#0d1424"; // sidebar background — matches redesign prototype's --ink-900
export const NAVY_SOFT = "#1c2c4a"; // sidebar hover

// Primary accent is a clean corporate blue (buttons, links, active nav).
export const ACCENT = "#2563eb"; // primary blue
export const ACCENT_DARK = "#1d4ed8";
export const ACCENT_SOFT = "#eff4ff";

// ===== Design-system tokens (Phase 1) =====
// Shared shadow scale used for the redesigned AppShell surfaces (topbar,
// sidebar-on-scroll, elevated cards in later phases). Additive only — no
// existing component relied on unnamed shadow values before this.
// Radius scale matching the redesign prototype's --r-sm/--r/--r-lg tokens.
// Cards were already at 12px (r-lg) before this change and are left as-is.
// Buttons/inputs move from the old blanket 10px down to 8px (r) below.
export const RADIUS_SM = 6;
export const RADIUS = 8;
export const RADIUS_LG = 12;

export const SHADOW_SM = "0 1px 2px rgba(15,27,51,0.05)";
export const SHADOW_MD = "0 4px 12px rgba(15,27,51,0.07)";
export const SHADOW_LG = "0 16px 40px rgba(15,27,51,0.14)";

// Status/semantic colors for badges and chips, matching the palette already
// used ad-hoc across existing pages (success/warning/error/info below) plus
// two extra hues (violet/teal) the redesigned components can use for
// additional status categories. Exported for reuse by future reusable
// components (Phase 2) — does not change any existing MUI palette value.
export const STATUS_COLORS = {
  ok: { main: "#059669", soft: "#ecfdf5" },
  warn: { main: "#d97706", soft: "#fffbeb" },
  danger: { main: "#dc2626", soft: "#fef2f2" },
  info: { main: "#2563eb", soft: "#eff6ff" },
  violet: { main: "#7c3aed", soft: "#f5f3ff" },
  teal: { main: "#0d9488", soft: "#f0fdfa" },
  neutral: { main: "#64748b", soft: "#f1f5f9" },
};

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: ACCENT,
      dark: ACCENT_DARK,
      light: "#60a5fa",
      contrastText: "#ffffff",
    },
    secondary: { main: NAVY },
    background: {
      default: "#f6f8fb",
      paper: "#ffffff",
    },
    text: {
      primary: "#0f172a",
      secondary: "#64748b",
    },
    divider: "#e6eaf0",
    success: { main: "#16a34a", light: "#f0fdf4" },
    warning: { main: "#d97706", light: "#fffbeb" },
    error: { main: "#dc2626", light: "#fef2f2" },
    info: { main: "#2563eb", light: "#eff6ff" },
  },
  typography: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
    h4: { fontWeight: 700, fontSize: "1.55rem", letterSpacing: "-0.5px" },
    h5: { fontWeight: 700, fontSize: "1.2rem", letterSpacing: "-0.3px" },
    h6: { fontWeight: 700, fontSize: "1.02rem" },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body2: { fontSize: "0.85rem" },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
  },
  shape: { borderRadius: RADIUS },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          paddingInline: 16,
          transition: "all 0.15s ease",
        },
        containedPrimary: {
          boxShadow: "0 1px 2px rgba(37,99,235,0.25)",
          "&:hover": { boxShadow: "0 4px 12px rgba(37,99,235,0.30)" },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        elevation1: {
          boxShadow:
            "0 1px 3px rgba(15,27,51,0.06), 0 1px 2px rgba(15,27,51,0.04)",
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: "1px solid #e6eaf0",
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(15,27,51,0.05)",
          transition: "box-shadow 0.2s ease, transform 0.2s ease",
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small", fullWidth: true },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
            borderColor: ACCENT,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": { backgroundColor: "#f8fafc" },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          fontSize: "0.72rem",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          color: "#64748b",
          backgroundColor: "#f8fafc",
        },
        root: { fontSize: "0.82rem", borderColor: "#eef1f5" },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 8 },
        sizeSmall: { fontSize: "0.72rem" },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600 },
      },
    },
  },
});

export default theme;
